import React, { useState, useEffect, useRef } from "react";
import { getAuth } from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom"; 
import { usePriceContext } from "../context/PriceContext";
import TvIcon from '@mui/icons-material/Tv';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import WarningIcon from '@mui/icons-material/Warning';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { IconButton, Tooltip, Chip } from "@mui/material";

// FIREBASE
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import SarrafBanner from '../assets/a.png';

const formatDateTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp * 1000);
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleDateString("tr-TR", { month: "long" });
  const dayName = date.toLocaleDateString("tr-TR", { weekday: "long" });
  const time = date.toLocaleTimeString("tr-TR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  return `${day} ${month} ${dayName} ${time}`;
};

// --- FİYAT HÜCRESİ BİLEŞENİ ---
const PriceCell = ({ data, isVitrin, isSmallFont = false, isTopTable = false }) => {
  if (!data) return <span>-</span>;
  const { value } = data;
  
  const color = isVitrin ? "#0c1220" : "#141a22";

  // YAZI BOYUTU VE KALINLIK AYARLARI BURADA
  let fontSize;
  if (isVitrin) {
      if (isTopTable) {
          fontSize = '4.0vh'; // Üst Tablo Fiyatları
      } else {
          fontSize = isSmallFont ? '3.5vh' : '4.5vh'; // Alt Tablo Fiyatları
      }
  } else {
      fontSize = isSmallFont ? '18px' : '22px';
  }
  
  // Kalınlık 700 (Bold) olarak ayarlandı
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', color: color }} className="gf-price-cell">
      <span style={{ fontSize: fontSize, fontWeight: '700', lineHeight: 1, letterSpacing: isVitrin ? '1px' : '0' }}>{value}</span>
    </div>
  );
};

const GuncelFiyatEkrani = ({ sube: _sube }) => {
  const { prices, errorMessage, systemTime } = usePriceContext(); 
  const location = useLocation();

  const [formuller, setFormuller] = useState([]); 
  const [genelAyarlar, setGenelAyarlar] = useState({ aktif: false });

  const [localTime, setLocalTime] = useState(new Date());
  const [priceData, setPriceData] = useState({});
  const [isVitrinMode, setIsVitrinMode] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const prevPrices = useRef({});
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const interval = setInterval(() => setLocalTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) navigate("/login");
    });
    return () => unsubscribe();
  }, [navigate, auth]);

  // URL'den vitrin modu kontrolü
  useEffect(() => {
    const currentUrl = window.location.href;
    if (currentUrl.includes("mode=tv")) {
      setIsVitrinMode(true);
    }
  }, [location]);

  // Firebase Verileri
  useEffect(() => {
    const unsubFormul = onSnapshot(collection(db, "formuller"), (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFormuller(list);
    });

    const unsubAyarlar = onSnapshot(doc(db, "ayarlar", "genel_durum"), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setGenelAyarlar({
            aktif: data.aktif,
            manuelHasAlis: parseFloat(data.manuelHasAlis || 0), manuelHasSatis: parseFloat(data.manuelHasSatis || 0),
            manuelOnsAlis: parseFloat(data.manuelOnsAlis || 0), manuelOnsSatis: parseFloat(data.manuelOnsSatis || 0),
            manuelGumUsOnsAlis: parseFloat(data.manuelGumUsOnsAlis || 0), manuelGumUsOnsSatis: parseFloat(data.manuelGumUsOnsSatis || 0),
            manuelDolarAlis: parseFloat(data.manuelDolarAlis || 0), manuelDolarSatis: parseFloat(data.manuelDolarSatis || 0),
            manuelEuroAlis: parseFloat(data.manuelEuroAlis || 0), manuelEuroSatis: parseFloat(data.manuelEuroSatis || 0),
            manuelPariteAlis: parseFloat(data.manuelPariteAlis || 0), manuelPariteSatis: parseFloat(data.manuelPariteSatis || 0)
        });
      }
    });
    return () => { unsubFormul(); unsubAyarlar(); };
  }, []);

  const calculateResult = (row) => {
      if (genelAyarlar.aktif) return calculateAcil(row);
      else return calculateNormal(row);
  };

  const calculateNormal = (formul) => {
    if (formul.isManual) return { alis: Number(formul.manualBuy), satis: Number(formul.manualSell) };
    const sourceData = prices.find(p => p.currencyCode === formul.kaynak);
    const baseAlis = parseFloat(sourceData?.buy) || 0;
    const baseSatis = parseFloat(sourceData?.sell) || 0;
    const hAlis = (baseAlis * parseFloat(formul.alisCarpan)) + parseFloat(formul.alisIscilik);
    const hSatis = (baseSatis * parseFloat(formul.satisCarpan)) + parseFloat(formul.satisIscilik);
    return { alis: hAlis, satis: hSatis };
  };

  const calculateAcil = (formul) => {
    let baseAlis = 0; let baseSatis = 0; 
    let activeFormul = formul; let katSayi = 1; let isChild = false; 
    const normalize = (text) => text ? text.toString().toLocaleLowerCase('tr-TR').trim() : "";
    const nameLower = normalize(formul.ad);

    if (nameLower.includes("eski ziynet") && !nameLower.includes("çeyrek")) {
        const parent = formuller.find(f => normalize(f.ad).includes("eski ziynet çeyrek"));
        if (parent) { activeFormul = parent; isChild = true; }
    } else if (nameLower.includes("yeni ziynet") && !nameLower.includes("çeyrek")) {
        const parent = formuller.find(f => normalize(f.ad).includes("yeni ziynet çeyrek"));
        if (parent) { activeFormul = parent; isChild = true; }
    } else if (nameLower.includes("gram") && nameLower.includes("24 ayar") && !nameLower.startsWith("1 gram")) {
        if (!nameLower.includes("0.25") && !nameLower.includes("0.50") && !nameLower.includes("0.5 gram")) {
             const parent = formuller.find(f => normalize(f.ad).includes("1 gram 24 ayar"));
             if (parent) { activeFormul = parent; isChild = true; }
        }
    }

    if (isChild) {
        if (nameLower.includes("beşli") || nameLower.includes("besli")) katSayi = 20;
        else if (nameLower.includes("gremse") || nameLower.includes("2.5'lu") || nameLower.includes("2,5'lu")) katSayi = 10;
        else if (nameLower.includes("tam")) katSayi = 4;
        else if (nameLower.includes("yarım") || nameLower.includes("yarim")) katSayi = 2;
        else if (nameLower.includes("100 gram")) katSayi = 100;
        else if (nameLower.includes("50 gram")) katSayi = 50;
        else if (nameLower.includes("20 gram")) katSayi = 20;
        else if (nameLower.includes("10 gram")) katSayi = 10;
        else if (nameLower.includes("5 gram")) katSayi = 5;
        else if (nameLower.includes("2.50") || nameLower.includes("2.5") || nameLower.includes("2,5")) katSayi = 2.5;
    }

    const effectiveSource = activeFormul.acilKaynak || 'MANUEL_HAS';
    
    if (effectiveSource === "OTO_EURKG_PARITE") {
        const pariteAlis = genelAyarlar.manuelPariteAlis || 1; const pariteSatis = genelAyarlar.manuelPariteSatis || 1;
        baseAlis = pariteAlis > 0 ? (genelAyarlar.manuelOnsAlis * 32.15) / pariteAlis : 0;
        baseSatis = pariteSatis > 0 ? (genelAyarlar.manuelOnsSatis * 32.15) / pariteSatis : 0;
    } 
    else if (effectiveSource === "OTO_GOLD_SILVER_RATIO") {
        baseAlis = genelAyarlar.manuelGumUsOnsAlis > 0 ? genelAyarlar.manuelOnsAlis / genelAyarlar.manuelGumUsOnsAlis : 0;
        baseSatis = genelAyarlar.manuelGumUsOnsSatis > 0 ? genelAyarlar.manuelOnsSatis / genelAyarlar.manuelGumUsOnsSatis : 0;
    }
    else if (effectiveSource === "OTO_GUMUS_TL") {
        baseAlis = (genelAyarlar.manuelGumUsOnsAlis * genelAyarlar.manuelDolarAlis) / 31.1035;
        baseSatis = (genelAyarlar.manuelGumUsOnsSatis * genelAyarlar.manuelDolarSatis) / 31.1035;
    }
    else if (effectiveSource === "MANUEL_GUMUS_ONS") {
        baseAlis = genelAyarlar.manuelGumUsOnsAlis; baseSatis = genelAyarlar.manuelGumUsOnsSatis;
    }
    else if (effectiveSource === "MANUEL_ONS") {
        baseAlis = genelAyarlar.manuelOnsAlis; baseSatis = genelAyarlar.manuelOnsSatis;
    } else if (effectiveSource === "MANUEL_DOLAR") {
        baseAlis = genelAyarlar.manuelDolarAlis; baseSatis = genelAyarlar.manuelDolarSatis;
    } else if (effectiveSource === "MANUEL_EURO") {
        baseAlis = genelAyarlar.manuelEuroAlis; baseSatis = genelAyarlar.manuelEuroSatis;
    } else {
        baseAlis = genelAyarlar.manuelHasAlis; baseSatis = genelAyarlar.manuelHasSatis;
    }

    const alisCarpan = parseFloat(activeFormul.acilAlisCarpan || ((effectiveSource.startsWith("OTO_")) ? 1 : 0));
    const satisCarpan = parseFloat(activeFormul.acilSatisCarpan || ((effectiveSource.startsWith("OTO_")) ? 1 : 0));

    const hAlis = baseAlis * alisCarpan * katSayi;
    const hSatis = baseSatis * satisCarpan * katSayi;

    return { alis: hAlis, satis: hSatis };
  };

  const toggleVitrinMode = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((e) => console.log(e));
      setIsVitrinMode(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsVitrinMode(false);
      }
    }
  };

  useEffect(() => {
    const handleFullScreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullScreen(false);
        setIsVitrinMode(false);
      } else {
        setIsFullScreen(true);
      }
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullScreenChange);
  }, []);

  useEffect(() => {
    if (isVitrinMode) {
      document.body.classList.add("vitrin-mode-active");
    } else {
      document.body.classList.remove("vitrin-mode-active");
    }

    return () => {
      document.body.classList.remove("vitrin-mode-active");
    };
  }, [isVitrinMode]);

  const formatNumber = (number, decimals = 0) => {
    if (typeof number !== "number" || isNaN(number)) return "-";
    return number.toFixed(decimals).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  const getCalculatedPrice = (nameKeyword) => {
      const normalize = (text) => text ? text.toString().toLocaleLowerCase('tr-TR').trim() : "";
      const keyword = normalize(nameKeyword);
      let found = formuller.find(f => normalize(f.ad) === keyword);
      if (!found) { found = formuller.find(f => normalize(f.ad).includes(keyword)); }
      if (found) return calculateResult(found);
      return { alis: 0, satis: 0 }; 
  };

  useEffect(() => {
    const prevPricesRef = prevPrices.current;
    
    const updatePrice = (key, currentValue, decimals = 0) => {
      prevPricesRef[key] = currentValue;
      return { value: formatNumber(currentValue, decimals) };
    };

    const usd = getCalculatedPrice("USD - Amerikan");
    const eur = getCalculatedPrice("EUR - Euro");
    const gbp = getCalculatedPrice("İngiliz Sterlini");
    const chf = getCalculatedPrice("İsviçre Frangı");
    const cad = getCalculatedPrice("Kanada");
    const sar = getCalculatedPrice("Riyal");
    const has = getCalculatedPrice("Has Altın") || getCalculatedPrice("Has"); 
    const ons = getCalculatedPrice("Ons"); 
    
    // HURDA (ASTAR) GRUBU
    const ayar22 = getCalculatedPrice("22 Ayar");
    const ayar21 = getCalculatedPrice("21 Ayar"); 
    const ayar18 = getCalculatedPrice("18 Ayar"); 
    const ayar14 = getCalculatedPrice("14 Ayar"); 
    const ayar8 = getCalculatedPrice("8 Ayar");

    const bilezik22 = getCalculatedPrice("22 Ayar Bilezik");
    const ata = getCalculatedPrice("Cumhuriyet Ata"); 
    const gumusTl = getCalculatedPrice("Gümüş TL");
    
    // SARRAFİYE
    const gram24 = getCalculatedPrice("1 Gram 24 Ayar");
    const gram22_ozel = getCalculatedPrice("1 Gram 22 Ayar");
    const gram22 = gram22_ozel.alis > 0 ? gram22_ozel : ayar22;

    const eskiCeyrek = getCalculatedPrice("Eski Ziynet Çeyrek");
    const yeniCeyrek = getCalculatedPrice("Yeni Ziynet Çeyrek");
    const eskiYarim = getCalculatedPrice("Eski Ziynet Yarım");
    const yeniYarim = getCalculatedPrice("Yeni Ziynet Yarım");
    const eskiTam = getCalculatedPrice("Eski Ziynet Tam");
    const yeniTam = getCalculatedPrice("Yeni Ziynet Tam");

    const newPriceData = {
      // DÖVİZLER (2 HANE)
      "usd-buy": updatePrice("usd-buy", usd.alis, 2), "usd-sell": updatePrice("usd-sell", usd.satis, 2),
      "eur-buy": updatePrice("eur-buy", eur.alis, 2), "eur-sell": updatePrice("eur-sell", eur.satis, 2),
      "gbp-buy": updatePrice("gbp-buy", gbp.alis, 2), "gbp-sell": updatePrice("gbp-sell", gbp.satis, 2),
      "chf-buy": updatePrice("chf-buy", chf.alis, 2), "chf-sell": updatePrice("chf-sell", chf.satis, 2),
      "cad-buy": updatePrice("cad-buy", cad.alis, 2), "cad-sell": updatePrice("cad-sell", cad.satis, 2),
      "sar-buy": updatePrice("sar-buy", sar.alis, 2), "sar-sell": updatePrice("sar-sell", sar.satis, 2),
      
      // ORTA SOL TABLO - AYAR ALTINLAR (0 HANE)
      "22-ayar-alis": updatePrice("22-ayar-alis", ayar22.alis, 0), "22-ayar-satis": updatePrice("22-ayar-satis", ayar22.satis, 0),
      "21-ayar-alis": updatePrice("21-ayar-alis", ayar21.alis, 0), "21-ayar-satis": updatePrice("21-ayar-satis", ayar21.satis, 0),
      "18-ayar-alis": updatePrice("18-ayar-alis", ayar18.alis, 0), "18-ayar-satis": updatePrice("18-ayar-satis", ayar18.satis, 0),
      "14-ayar-alis": updatePrice("14-ayar-alis", ayar14.alis, 0), "14-ayar-satis": updatePrice("14-ayar-satis", ayar14.satis, 0),
      "8-ayar-alis": updatePrice("8-ayar-alis", ayar8.alis, 0), "8-ayar-satis": updatePrice("8-ayar-satis", ayar8.satis, 0),
      
      // ORTA SAĞ (HAS/ONS 2 HANE - BİLEZİK DÜZELTİLDİ: 0 HANE)
      "22-ayar-bilezik-al": updatePrice("22-ayar-bilezik-al", bilezik22.alis, 0), 
      "22-ayar-bilezik-sa": updatePrice("22-ayar-bilezik-sa", bilezik22.satis, 0), 
      
      "has-altin-al": updatePrice("has-altin-al", has.alis, 2), "has-altin-sa": updatePrice("has-altin-sa", has.satis, 2),
      "ons-al": updatePrice("ons-al", ons.alis, 2), "ons-sa": updatePrice("ons-sa", ons.satis, 2),
      "ata-al": updatePrice("ata-al", ata.alis, 0), "ata-sa": updatePrice("ata-sa", ata.satis, 0),
      "gumus-tl-al": updatePrice("gumus-tl-al", gumusTl.alis, 2), "gumus-tl-sa": updatePrice("gumus-tl-sa", gumusTl.satis, 2),
      
      // SOL ALT (Gramlar) - (0 HANE)
      "24-gram-al": updatePrice("24-gram-al", gram24.alis, 0), "24-gram-sa": updatePrice("24-gram-sa", gram24.satis, 0),
      "24-yarim-gram-al": updatePrice("24-yarim-gram-al", getCalculatedPrice("0.50 Gram 24 Ayar").alis || gram24.alis/2, 0), "24-yarim-gram-sa": updatePrice("24-yarim-gram-sa", getCalculatedPrice("0.50 Gram 24 Ayar").satis || gram24.satis/2, 0),
      "24-ceyrek-gram-al": updatePrice("24-ceyrek-gram-al", getCalculatedPrice("0.25 Gram 24 Ayar").alis || gram24.alis/4, 0), "24-ceyrek-gram-sa": updatePrice("24-ceyrek-gram-sa", getCalculatedPrice("0.25 Gram 24 Ayar").satis || gram24.satis/4, 0),
      
      // 22 Ayar 1 Gram
      "22-gram-al": updatePrice("22-gram-al", gram22.alis, 0), "22-gram-sa": updatePrice("22-gram-sa", gram22.satis, 0),
      "22-yarim-gram-al": updatePrice("22-yarim-gram-al", getCalculatedPrice("0.50 Gram 22 Ayar").alis || gram22.alis/2, 0), "22-yarim-gram-sa": updatePrice("22-yarim-gram-sa", getCalculatedPrice("0.50 Gram 22 Ayar").satis || gram22.satis/2, 0),
      "22-ceyrek-gram-al": updatePrice("22-ceyrek-gram-al", getCalculatedPrice("0.25 Gram 22 Ayar").alis || gram22.alis/4, 0), "22-ceyrek-gram-sa": updatePrice("22-ceyrek-gram-sa", getCalculatedPrice("0.25 Gram 22 Ayar").satis || gram22.satis/4, 0),
      
      // SAĞ ALT (Ziynetler)
      "ec-al": updatePrice("ec-al", eskiCeyrek.alis, 0), "ec-sa": updatePrice("ec-sa", eskiCeyrek.satis, 0),
      "yc-al": updatePrice("yc-al", yeniCeyrek.alis, 0), "yc-sa": updatePrice("yc-sa", yeniCeyrek.satis, 0),
      "ey-al": updatePrice("ey-al", eskiYarim.alis > 0 ? eskiYarim.alis : eskiCeyrek.alis * 2, 0), "ey-sa": updatePrice("ey-sa", eskiYarim.satis > 0 ? eskiYarim.satis : eskiCeyrek.satis * 2, 0),
      "yy-al": updatePrice("yy-al", yeniYarim.alis > 0 ? yeniYarim.alis : yeniCeyrek.alis * 2, 0), "yy-sa": updatePrice("yy-sa", yeniYarim.satis > 0 ? yeniYarim.satis : yeniCeyrek.satis * 2, 0),
      "et-al": updatePrice("et-al", eskiTam.alis > 0 ? eskiTam.alis : eskiCeyrek.alis * 4, 0), "et-sa": updatePrice("et-sa", eskiTam.satis > 0 ? eskiTam.satis : eskiCeyrek.satis * 4, 0),
      "yt-al": updatePrice("yt-al", yeniTam.alis > 0 ? yeniTam.alis : yeniCeyrek.alis * 4, 0), "yt-sa": updatePrice("yt-sa", yeniTam.satis > 0 ? yeniTam.satis : yeniCeyrek.satis * 4, 0),
    };
    setPriceData(newPriceData);
  }, [prices, formuller, genelAyarlar]); 

  const isciligeGore = (
    <span style={{
      fontSize: isVitrinMode ? '2.5vh' : '18px',
      color: isVitrinMode ? '#7c5e12' : '#7a6220',
      fontStyle: 'italic',
      fontWeight: 'bold'
    }}
    >
      İşçiliğe Göre Belirlenir
    </span>
  );

  return (
    <div
      className="gf-page"
      style={{ 
      fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
      background: isVitrinMode
        ? 'linear-gradient(165deg, #f5f2eb 0%, #ebe4d8 42%, #e0d8cc 100%)'
        : '#ffffff',
      color: isVitrinMode ? '#1a1814' : '#1a1814',
      margin: 0, 
      padding: isVitrinMode ? "10px 0" : "0",
      minHeight: isVitrinMode ? '100vh' : 'auto',
      ...(isVitrinMode ? {
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 99999, 
        padding: '30px 40px', 
        boxSizing: 'border-box', overflow: 'hidden'
      } : {})
    }}>
      
      <div className="gf-toolbar" style={{ position: 'absolute', top: isVitrinMode ? 25 : 10, right: isVitrinMode ? 25 : 10, zIndex: 100000, display: 'flex', gap: '10px' }}>
        
        {errorMessage && (
            <Tooltip title="Bağlantı Sorunu - Fiyatlar Güncel Olmayabilir">
                <Chip icon={<WifiOffIcon style={{ color: '#fff' }} />} label="BAĞLANTI" sx={{ fontWeight: 700, color: '#fff', bgcolor: 'rgba(180, 83, 9, 0.92)', border: '1px solid rgba(253, 224, 71, 0.35)' }} />
            </Tooltip>
        )}

        {genelAyarlar.aktif && (
            <Chip icon={<WarningIcon style={{ color: '#fff' }} />} label="MANUEL MOD" sx={{ fontWeight: 700, color: '#fff', bgcolor: 'rgba(127, 29, 29, 0.92)', border: '1px solid rgba(252, 165, 165, 0.35)' }} />
        )}
        
        <Tooltip title={isVitrinMode ? "Tam Ekrandan Çık" : "Vitrin Modunu Aç"}>
          <IconButton 
            onClick={toggleVitrinMode} 
            sx={{
              bgcolor: isVitrinMode ? 'rgba(15, 23, 42, 0.08)' : 'rgba(212, 175, 98, 0.12)',
              color: '#0f172a',
              border: '1px solid rgba(212, 175, 98, 0.4)',
              '&:hover': { bgcolor: isVitrinMode ? 'rgba(15, 23, 42, 0.14)' : 'rgba(212, 175, 98, 0.22)' }
            }}
          >
            {isFullScreen ? <CloseFullscreenIcon /> : <TvIcon />}
          </IconButton>
        </Tooltip>
      </div>

      <style>
        {`
          .table-container {
            max-width: ${isVitrinMode ? '100%' : '2000px'};
            margin: auto;
            background: ${isVitrinMode ? 'transparent' : 'linear-gradient(180deg, rgba(252, 251, 248, 0.98) 0%, rgba(244, 241, 235, 0.96) 100%)'};
            color: ${isVitrinMode ? '#1a1814' : '#1a1814'};
            border-radius: 16px;
            padding: ${isVitrinMode ? '0' : '14px'};
            box-shadow: ${isVitrinMode ? 'none' : '0 20px 48px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255,255,255,0.75)'};
            border: ${isVitrinMode ? 'none' : '1px solid rgba(212, 175, 98, 0.2)'};
            overflow-x: auto;
            height: ${isVitrinMode ? '100%' : 'auto'};
            display: flex;
            flex-direction: column;
            justify-content: space-between;
          }
          table { width: 100%; border-collapse: collapse; margin-bottom: ${isVitrinMode ? '1vh' : '12px'}; border: ${isVitrinMode ? 'none' : '1px solid rgba(212, 175, 98, 0.18)'}; }
          .bottom-table table { margin-bottom: 0; }
          
          th, td { padding: ${isVitrinMode ? '0.3vh 0.5vw' : '8px 11px'}; text-align: center; border: ${isVitrinMode ? '2px solid rgba(226, 216, 200, 0.85)' : '1px solid rgba(212, 175, 98, 0.15)'}; font-weight: 700; font-size: ${isVitrinMode ? '4.0vh' : '21px'}; line-height: 1.2; white-space: nowrap; color: ${isVitrinMode ? '#1a1814' : '#2a2620'}; }
          
          th { background: ${isVitrinMode ? 'linear-gradient(180deg, #1a1f28 0%, #0e1218 100%)' : 'linear-gradient(180deg, #1a1f28 0%, #121820 100%)'}; color: #f4f1eb; text-transform: uppercase; font-size: ${isVitrinMode ? '3.0vh' : '13px'}; letter-spacing: 0.06em; border-bottom: ${isVitrinMode ? '4px solid rgba(212, 175, 98, 0.75)' : '2px solid rgba(212, 175, 98, 0.45)'}; }
          
          .top-table th, .top-table td {
             padding: ${isVitrinMode ? '0.2vh 0.2vw' : '8px 5px'}; 
             font-size: ${isVitrinMode ? '3.0vh' : '16px'};
          }

          tbody tr:nth-child(odd) { background-color: ${isVitrinMode ? 'rgba(255,255,255,0.45)' : 'rgba(250, 248, 244, 0.95)'}; }
          tbody tr:nth-child(even) { background-color: ${isVitrinMode ? 'rgba(0,0,0,0.03)' : 'rgba(238, 232, 222, 0.65)'}; }
          
          .middle-table, .bottom-table { display: flex; justify-content: space-between; gap: ${isVitrinMode ? '2vw' : '14px'}; flex: 1; }
          .middle-table .left-table, .middle-table .right-table, .bottom-table .left-table, .bottom-table .right-table { width: 49%; }
          .header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: ${isVitrinMode ? '2vh' : '10px'}; border-bottom: ${isVitrinMode ? '2px solid rgba(212, 175, 98, 0.35)' : '1px solid rgba(212, 175, 98, 0.22)'}; padding-bottom: ${isVitrinMode ? '1vh' : '12px'}; flex-shrink: 0; }
          
          #last-update { font-size: ${isVitrinMode ? '2.2vh' : '15px'}; color: ${isVitrinMode ? '#4a453c' : '#3d3830'}; margin: 0; text-align: right; flex: 1; padding-right: ${isVitrinMode ? '10px' : '0'}; font-weight: 500; letter-spacing: 0.02em; }
          #current-time { font-size: ${isVitrinMode ? '2.2vh' : '15px'}; font-weight: 700; color: ${isVitrinMode ? '#4a453c' : '#3d3830'}; text-align: left; flex: 1; padding-left: ${isVitrinMode ? '10px' : '0'}; letter-spacing: 0.02em; }
          
          .logo-text { font-family: 'Cinzel Decorative', serif; font-size: ${isVitrinMode ? '6.5vh' : '30px'}; color: ${isVitrinMode ? '#6b5420' : '#5c4818'}; margin: 0; font-weight: 400; letter-spacing: 8px; flex: 2; text-align: center; text-shadow: ${isVitrinMode ? '0 1px 0 rgba(255,255,255,0.5)' : '0 1px 0 rgba(255,255,255,0.6)'}; display: flex; align-items: center; justify-content: center; }
          .logo-text img { display: block; max-width: min(92vw, 720px); width: auto; object-fit: contain; }
          body.vitrin-mode-active .MuiSpeedDial-root {
            display: none !important;
          }

          /* --- MOBİL: Güncel Fiyat (vitrin modu hariç; masaüstü stillerine dokunulmaz) --- */
          @media (max-width: 600px) {
            body:not(.vitrin-mode-active) .gf-page {
              width: 100% !important;
              max-width: 100% !important;
              min-width: 0 !important;
              overflow-x: hidden !important;
              box-sizing: border-box !important;
            }

            body:not(.vitrin-mode-active) .gf-page .table-container {
              max-width: 100% !important;
              min-width: 0 !important;
              padding: 8px 4px 80px !important;
              overflow-x: hidden !important;
              box-sizing: border-box !important;
              border-radius: 12px !important;
            }

            body:not(.vitrin-mode-active) .gf-toolbar {
              top: 4px !important;
              right: 4px !important;
              gap: 6px !important;
              flex-wrap: wrap !important;
              justify-content: flex-end !important;
              max-width: calc(100% - 8px) !important;
            }

            body:not(.vitrin-mode-active) .gf-toolbar .MuiChip-root {
              font-size: 10px !important;
              height: 28px !important;
            }

            body:not(.vitrin-mode-active) .gf-toolbar .MuiIconButton-root {
              width: 36px !important;
              height: 36px !important;
            }

            body:not(.vitrin-mode-active) .gf-page .header-row {
              flex-direction: column !important;
              align-items: center !important;
              gap: 8px !important;
              margin-bottom: 12px !important;
              padding-bottom: 10px !important;
              padding-top: 36px !important;
            }

            body:not(.vitrin-mode-active) .gf-page #current-time,
            body:not(.vitrin-mode-active) .gf-page #last-update {
              flex: none !important;
              width: 100% !important;
              text-align: center !important;
              font-size: 12px !important;
              padding: 0 !important;
              line-height: 1.35 !important;
            }

            body:not(.vitrin-mode-active) .gf-page .logo-text {
              flex: none !important;
              width: 100% !important;
            }

            body:not(.vitrin-mode-active) .gf-page .logo-text img {
              height: 42px !important;
              max-height: 48px !important;
              max-width: 100% !important;
            }

            body:not(.vitrin-mode-active) .gf-page .gf-top-table-wrap {
              overflow-x: auto !important;
              max-width: 100% !important;
              min-width: 0 !important;
              -webkit-overflow-scrolling: touch;
              margin-bottom: 12px !important;
            }

            body:not(.vitrin-mode-active) .gf-page .gf-top-table-wrap .top-table {
              min-width: 720px !important;
              margin-bottom: 0 !important;
            }

            body:not(.vitrin-mode-active) .gf-page .middle-table,
            body:not(.vitrin-mode-active) .gf-page .bottom-table {
              flex-direction: column !important;
              gap: 12px !important;
            }

            body:not(.vitrin-mode-active) .gf-page .middle-table .left-table,
            body:not(.vitrin-mode-active) .gf-page .middle-table .right-table,
            body:not(.vitrin-mode-active) .gf-page .bottom-table .left-table,
            body:not(.vitrin-mode-active) .gf-page .bottom-table .right-table {
              width: 100% !important;
            }

            body:not(.vitrin-mode-active) .gf-page table {
              margin-bottom: 0 !important;
            }

            body:not(.vitrin-mode-active) .gf-page th,
            body:not(.vitrin-mode-active) .gf-page td {
              font-size: 12px !important;
              padding: 6px 4px !important;
              white-space: normal !important;
              line-height: 1.25 !important;
            }

            body:not(.vitrin-mode-active) .gf-page th {
              font-size: 10px !important;
              letter-spacing: 0.04em !important;
            }

            body:not(.vitrin-mode-active) .gf-page .top-table th,
            body:not(.vitrin-mode-active) .gf-page .top-table td {
              font-size: 10px !important;
              padding: 5px 3px !important;
              white-space: nowrap !important;
            }

            body:not(.vitrin-mode-active) .gf-page .gf-price-cell span {
              font-size: 13px !important;
              letter-spacing: 0 !important;
            }

            body:not(.vitrin-mode-active) .gf-page .gf-top-table-wrap .gf-price-cell span {
              font-size: 11px !important;
            }
          }
        `}
      </style>
      <link href="https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700&display=swap" rel="stylesheet" />
      
      <div className="table-container">
        <div className="header-row">
          <p id="current-time">{formatDateTime(Math.floor(localTime.getTime() / 1000))}</p>
          <div className="logo-text" role="img" aria-label="SARRAF">
            <img
              src={SarrafBanner}
              alt=""
              style={{
                height: isVitrinMode ? '9.72vh' : '54px',
                maxHeight: isVitrinMode ? '13.5vh' : '72px'
              }}
            />
          </div>
          <p id="last-update">Son Güncelleme: {systemTime ? formatDateTime(systemTime) : "Bekleniyor..."}</p>
        </div>
        <div className="gf-top-table-wrap">
        <table className="top-table">
          <tbody>
            <tr>
              <th colSpan="2">DOLAR</th>
              <th colSpan="2">EURO</th>
              <th colSpan="2">İNGİLİZ STERLİNİ</th>
              <th colSpan="2">İSVİÇRE FRANGI</th>
              <th colSpan="2">KANADA DOLARI</th>
              <th colSpan="2">SUUD RİYALİ</th>
            </tr>
            <tr>
              {/* DÜZELTME: isTopTable={true} prop'u eklendi */}
              <td id="usd-buy"><PriceCell data={priceData["usd-buy"]} isVitrin={isVitrinMode} isTopTable={true} /></td>
              <td id="usd-sell"><PriceCell data={priceData["usd-sell"]} isVitrin={isVitrinMode} isTopTable={true} /></td>
              <td id="eur-buy"><PriceCell data={priceData["eur-buy"]} isVitrin={isVitrinMode} isTopTable={true} /></td>
              <td id="eur-sell"><PriceCell data={priceData["eur-sell"]} isVitrin={isVitrinMode} isTopTable={true} /></td>
              <td id="gbp-buy"><PriceCell data={priceData["gbp-buy"]} isVitrin={isVitrinMode} isTopTable={true} /></td>
              <td id="gbp-sell"><PriceCell data={priceData["gbp-sell"]} isVitrin={isVitrinMode} isTopTable={true} /></td>
              <td id="chf-buy"><PriceCell data={priceData["chf-buy"]} isVitrin={isVitrinMode} isTopTable={true} /></td>
              <td id="chf-sell"><PriceCell data={priceData["chf-sell"]} isVitrin={isVitrinMode} isTopTable={true} /></td>
              <td id="cad-buy"><PriceCell data={priceData["cad-buy"]} isVitrin={isVitrinMode} isTopTable={true} /></td>
              <td id="cad-sell"><PriceCell data={priceData["cad-sell"]} isVitrin={isVitrinMode} isTopTable={true} /></td>
              <td id="sar-buy"><PriceCell data={priceData["sar-buy"]} isVitrin={isVitrinMode} isTopTable={true} /></td>
              <td id="sar-sell"><PriceCell data={priceData["sar-sell"]} isVitrin={isVitrinMode} isTopTable={true} /></td>
            </tr>
          </tbody>
        </table>
        </div>
        <div className="middle-table">
          <table className="left-table">
            <tbody>
              <tr>
                <th></th>
                <th>ALIŞ</th>
                <th>SATIŞ</th>
              </tr>
              <tr>
                <td>22 AYAR</td>
                <td><PriceCell data={priceData["22-ayar-alis"]} isVitrin={isVitrinMode} /></td>
                <td>{isVitrinMode ? isciligeGore : <PriceCell data={priceData["22-ayar-satis"]} isSmallFont={false} />}</td>
              </tr>
              <tr>
                <td>21 AYAR</td>
                <td><PriceCell data={priceData["21-ayar-alis"]} isVitrin={isVitrinMode} /></td>
                <td>{isVitrinMode ? isciligeGore : <PriceCell data={priceData["21-ayar-satis"]} isSmallFont={false} />}</td>
              </tr>
              <tr>
                <td>18 AYAR</td>
                <td><PriceCell data={priceData["18-ayar-alis"]} isVitrin={isVitrinMode} /></td>
                <td>{isVitrinMode ? isciligeGore : <PriceCell data={priceData["18-ayar-satis"]} isSmallFont={false} />}</td>
              </tr>
              <tr>
                <td>14 AYAR</td>
                <td><PriceCell data={priceData["14-ayar-alis"]} isVitrin={isVitrinMode} /></td>
                <td>{isVitrinMode ? isciligeGore : <PriceCell data={priceData["14-ayar-satis"]} isSmallFont={false} />}</td>
              </tr>
              <tr>
                <td>8 AYAR</td>
                <td><PriceCell data={priceData["8-ayar-alis"]} isVitrin={isVitrinMode} /></td>
                <td>{isVitrinMode ? isciligeGore : <PriceCell data={priceData["8-ayar-satis"]} isSmallFont={false} />}</td>
              </tr>
            </tbody>
          </table>
          <table className="right-table">
            <tbody>
              <tr>
                <th></th>
                <th>ALIŞ</th>
                <th>SATIŞ</th>
              </tr>
              <tr>
                <td>HAS ALTIN</td>
                <td><PriceCell data={priceData["has-altin-al"]} isVitrin={isVitrinMode} /></td>
                <td><PriceCell data={priceData["has-altin-sa"]} isVitrin={isVitrinMode} /></td>
              </tr>
              <tr>
                <td>ONS</td>
                <td><PriceCell data={priceData["ons-al"]} isVitrin={isVitrinMode} /></td>
                <td><PriceCell data={priceData["ons-sa"]} isVitrin={isVitrinMode} /></td>
              </tr>
              <tr>
                <td>22 AYAR BİLEZİK</td>
                <td><PriceCell data={priceData["22-ayar-bilezik-al"]} isVitrin={isVitrinMode} /></td>
                <td><PriceCell data={priceData["22-ayar-bilezik-sa"]} isVitrin={isVitrinMode} /></td>
              </tr>
              <tr>
                <td>CUMHURİYET ATA</td>
                <td><PriceCell data={priceData["ata-al"]} isVitrin={isVitrinMode} /></td>
                <td><PriceCell data={priceData["ata-sa"]} isVitrin={isVitrinMode} /></td>
              </tr>
              <tr>
                <td>GÜMÜŞ TL</td>
                <td><PriceCell data={priceData["gumus-tl-al"]} isVitrin={isVitrinMode} /></td>
                <td><PriceCell data={priceData["gumus-tl-sa"]} isVitrin={isVitrinMode} /></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="bottom-table">
          <table className="left-table">
            <tbody>
              <tr>
                <th></th>
                <th>ALIŞ</th>
                <th>SATIŞ</th>
              </tr>
              <tr>
                <td>24 AYAR 1 GRAM</td>
                <td><PriceCell data={priceData["24-gram-al"]} isVitrin={isVitrinMode} /></td>
                <td><PriceCell data={priceData["24-gram-sa"]} isVitrin={isVitrinMode} /></td>
              </tr>
              <tr>
                <td>22 AYAR 1 GRAM</td>
                <td><PriceCell data={priceData["22-gram-al"]} isVitrin={isVitrinMode} /></td>
                <td><PriceCell data={priceData["22-gram-sa"]} isVitrin={isVitrinMode} /></td>
              </tr>
              <tr>
                <td>24 AYAR 0.5 GRAM</td>
                <td><PriceCell data={priceData["24-yarim-gram-al"]} isVitrin={isVitrinMode} /></td>
                <td><PriceCell data={priceData["24-yarim-gram-sa"]} isVitrin={isVitrinMode} /></td>
              </tr>
              <tr>
                <td>22 AYAR 0.5 GRAM</td>
                <td><PriceCell data={priceData["22-yarim-gram-al"]} isVitrin={isVitrinMode} /></td>
                <td><PriceCell data={priceData["22-yarim-gram-sa"]} isVitrin={isVitrinMode} /></td>
              </tr>
              <tr>
                <td>24 AYAR 0.25 GRAM</td>
                <td><PriceCell data={priceData["24-ceyrek-gram-al"]} isVitrin={isVitrinMode} /></td>
                <td><PriceCell data={priceData["24-ceyrek-gram-sa"]} isVitrin={isVitrinMode} /></td>
              </tr>
              <tr>
                <td>22 AYAR 0.25 GRAM</td>
                <td><PriceCell data={priceData["22-ceyrek-gram-al"]} isVitrin={isVitrinMode} /></td>
                <td><PriceCell data={priceData["22-ceyrek-gram-sa"]} isVitrin={isVitrinMode} /></td>
              </tr>
            </tbody>
          </table>
          <table className="right-table">
            <tbody>
              <tr>
                <th></th>
                <th>ALIŞ</th>
                <th>SATIŞ</th>
              </tr>
              <tr>
                <td>ESKİ ÇEYREK</td>
                <td><PriceCell data={priceData["ec-al"]} isVitrin={isVitrinMode} /></td>
                <td><PriceCell data={priceData["ec-sa"]} isVitrin={isVitrinMode} /></td>
              </tr>
              <tr>
                <td>YENİ ÇEYREK</td>
                <td><PriceCell data={priceData["yc-al"]} isVitrin={isVitrinMode} /></td>
                <td><PriceCell data={priceData["yc-sa"]} isVitrin={isVitrinMode} /></td>
              </tr>
              <tr>
                <td>ESKİ YARIM</td>
                <td><PriceCell data={priceData["ey-al"]} isVitrin={isVitrinMode} /></td>
                <td><PriceCell data={priceData["ey-sa"]} isVitrin={isVitrinMode} /></td>
              </tr>
              <tr>
                <td>YENİ YARIM</td>
                <td><PriceCell data={priceData["yy-al"]} isVitrin={isVitrinMode} /></td>
                <td><PriceCell data={priceData["yy-sa"]} isVitrin={isVitrinMode} /></td>
              </tr>
              <tr>
                <td>ESKİ TAM</td>
                <td><PriceCell data={priceData["et-al"]} isVitrin={isVitrinMode} /></td>
                <td><PriceCell data={priceData["et-sa"]} isVitrin={isVitrinMode} /></td>
              </tr>
              <tr>
                <td>YENİ TAM</td>
                <td><PriceCell data={priceData["yt-al"]} isVitrin={isVitrinMode} /></td>
                <td><PriceCell data={priceData["yt-sa"]} isVitrin={isVitrinMode} /></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default GuncelFiyatEkrani;